import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ItemType } from "sprout-graphql";
import { useCallback, useMemo } from "react";
import { Droppable } from "react-beautiful-dnd";
import { getItemUrl } from "sprout-lib";
import { useNavigationList } from "@sprout/list-navigation";
import useCurrentUser from "@sprout/client-graphql/dist/useCurrentUser";
import { useHistory } from "react-router-dom";
import useAssignMany from "@sprout/client-components/dist/Dialog_next/AssignmentDialog/graphql/mutations/useAssignMany";
import { BASE_ACTIONS, getPostAuthorization, usePostSingularAction } from "@sprout/client-components/dist/Post";
import { useFnRef } from "sprout-hooks";
import { CardsWrapper } from "@sprout/client-components/dist/Cards";
import useSetWhenForItems from "@sprout/client-graphql/dist/mutations/useSetWhenForItems";
import { usePopoverRegistry } from "sprout-ui-dom";
import { getLocalTimeZone, parseAbsolute, toCalendarDate, today } from "@internationalized/date";
import { useTopicPage } from "../../TopicPageContext";
import TopicPageNewPostButton from "../../TopicPageNewPostButton";
import { useTopicPageViewsTopicEditor } from "../TopicPageViewsTopicEditorContext";
import TopicPageViewsTopicListPostsPost from "./TopicPageViewsTopicListPostsPost";
const SUPPORTED_ACTIONS = [
    ...BASE_ACTIONS,
    "SELF_ASSIGN",
    "FOCUS_DIVIDER",
    "FOCUS_NOTE",
    "WHEN_TODAY",
    "WHEN_CLEAR",
    "DEADLINE",
    "WHEN", 
];
const TopicPageViewsTopicListPosts = (props)=>{
    const { topicId , topicNodeId , hasMorePosts , posts , context , prepend , mb , isSortEnabled , __hideLater ,  } = props;
    const history = useHistory();
    const { isPartOfTopic , isOwnerOrAdmin  } = useTopicPage();
    const currentUser = useCurrentUser();
    /**
   * Why is this needed? To handle the appearances of "optimisticResponse".
   * `MoveItemsDialog` will optimistically return posts with
   * the new `parentTopicId`.
   *
   * So by filtering, we remove all the moved posts,since they no longer have
   * the same `parentTopicId`.
   *
   * We are also removing all "later" posts
   */ const filteredPosts = useMemo(()=>{
        var ref;
        const timezone = (ref = currentUser === null || currentUser === void 0 ? void 0 : currentUser.timezone) !== null && ref !== void 0 ? ref : getLocalTimeZone();
        return posts.filter((post)=>{
            if (__hideLater) {
                const whenOrDeadline = post.when || post.deadline;
                if (!whenOrDeadline) return true;
                if (toCalendarDate(parseAbsolute(whenOrDeadline, timezone)).compare(today(timezone)) > 0) return false;
            }
            if (post.parentTopicId === topicId) return true;
            return false;
        });
    }, [
        __hideLater,
        currentUser === null || currentUser === void 0 ? void 0 : currentUser.timezone,
        posts,
        topicId
    ]);
    const { dispatch  } = useTopicPageViewsTopicEditor();
    const users = useMemo(()=>currentUser ? [
            currentUser
        ] : [], [
        currentUser
    ]);
    const assignMany = useAssignMany(users);
    const assignManyRef = useFnRef(assignMany);
    const setWhenForItems = useSetWhenForItems();
    const getPopoverRegistry = usePopoverRegistry();
    const currentUserId = currentUser === null || currentUser === void 0 ? void 0 : currentUser.id;
    const handleSingularAction = usePostSingularAction(useCallback((command, activeItem)=>{
        if (!activeItem) return false;
        const authorization = getPostAuthorization({
            post: activeItem,
            currentUserId
        });
        if (command === "FOCUS_DIVIDER") {
            if (activeItem.type !== ItemType.Divider || !authorization.edit) return false;
            dispatch({
                type: "SET_EDITOR",
                payload: {
                    editor: "EDIT_DIVIDER",
                    editorPosition: activeItem.id
                }
            });
            return true;
        }
        if (activeItem.type !== ItemType.Post) return false;
        switch(command){
            case "SELF_ASSIGN":
                {
                    if (!isOwnerOrAdmin || typeof currentUserId === "undefined") return false;
                    const assigned = !!activeItem.assignments.map(({ assignee  })=>assignee).find((user)=>{
                        return (user === null || user === void 0 ? void 0 : user.id) === currentUserId;
                    });
                    assignManyRef.current({
                        assigneesToAdd: !assigned ? [
                            currentUserId
                        ] : [],
                        assigneesToRemove: assigned ? [
                            currentUserId
                        ] : [],
                        posts: [
                            activeItem
                        ]
                    });
                    return true;
                }
            case "WHEN":
            case "DEADLINE":
                {
                    if (!isOwnerOrAdmin) return false;
                    const cache = getPopoverRegistry();
                    const handlers = cache[`${activeItem.id}#${command.toLowerCase()}`];
                    handlers === null || handlers === void 0 ? void 0 : handlers.onOpen();
                    return true;
                }
            case "WHEN_TODAY":
                if (!isOwnerOrAdmin) return false;
                setWhenForItems({
                    items: [
                        activeItem
                    ],
                    today: true
                });
                return true;
            case "WHEN_CLEAR":
                if (!isOwnerOrAdmin) return false;
                setWhenForItems({
                    items: [
                        activeItem
                    ],
                    when: null,
                    whenReminder: null
                });
                return true;
            case "FOCUS_NOTE":
                {
                    var ref;
                    const isPostEditable = isOwnerOrAdmin || isPartOfTopic && ((ref = activeItem.owner) === null || ref === void 0 ? void 0 : ref.id) === currentUserId;
                    if (!isPostEditable) return false;
                    if (command === "FOCUS_NOTE") history.push({
                        pathname: getItemUrl(activeItem),
                        state: {
                            focusNoteEditor: true
                        }
                    });
                    return true;
                }
            default:
                return false;
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        currentUserId,
        dispatch,
        history,
        isOwnerOrAdmin,
        isPartOfTopic
    ]));
    const { Provider , navProps  } = useNavigationList(`TopicPageViewsTopicPosts|${topicId}`, useMemo(()=>({
            actions: SUPPORTED_ACTIONS,
            multiselect: true,
            performSingularAction: handleSingularAction
        }), [
        handleSingularAction
    ]));
    const postsRenderFn = useCallback((dropProvided)=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(CardsWrapper, {
                    mb: mb,
                    ref: (element)=>{
                        dropProvided.innerRef(element);
                        navProps.ref.current = element;
                    },
                    children: [
                        filteredPosts.map((post, index)=>/*#__PURE__*/ _jsx(TopicPageViewsTopicListPostsPost, {
                                topicId: topicId,
                                topicNodeId: topicNodeId,
                                index: index,
                                isSortEnabled: isSortEnabled,
                                post: post,
                                context: context
                            }, post.nodeId)),
                        dropProvided.placeholder
                    ]
                }),
                isPartOfTopic && /*#__PURE__*/ _jsx(TopicPageNewPostButton, {
                    topicId: topicId,
                    topicNodeId: topicNodeId,
                    context: context,
                    /**
               * Do not show this button if there's no posts, or when
               * `hasMorePosts: true`
               */ explicitlyHidden: filteredPosts.length === 0 || hasMorePosts
                })
            ]
        });
    }, [
        mb,
        filteredPosts,
        isPartOfTopic,
        topicId,
        topicNodeId,
        context,
        hasMorePosts,
        navProps.ref,
        isSortEnabled, 
    ]);
    return /*#__PURE__*/ _jsxs(Provider, {
        children: [
            prepend,
            /*#__PURE__*/ _jsx(Droppable, {
                type: ItemType.Post,
                droppableId: JSON.stringify({
                    id: topicId,
                    nodeId: topicNodeId,
                    type: ItemType.Post
                }),
                isDropDisabled: !isSortEnabled,
                children: postsRenderFn
            })
        ]
    });
};
export default TopicPageViewsTopicListPosts;
